import React from 'react';
import { Link } from 'react-router-dom';

const TermsOfServicePage: React.FC = () => {
  const lastUpdated = 'December 9, 2025';

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      <div className="mb-8">
        <Link
          to="/"
          className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 text-sm"
        >
          &larr; Back to Home
        </Link>
      </div>

      <article className="prose prose-secondary dark:prose-invert max-w-none">
        <h1 className="text-3xl font-bold text-secondary-900 dark:text-secondary-100 mb-2">
          Terms of Service
        </h1>
        <p className="text-secondary-500 dark:text-secondary-400 text-sm mb-8">
          Last updated: {lastUpdated}
        </p>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-secondary-800 dark:text-secondary-200 mb-4">
            1. Acceptance of Terms
          </h2>
          <p className="text-secondary-700 dark:text-secondary-300 mb-4">
            By accessing or using the Agent Designer platform (the "Service")
            operated by GraymatterLab ("we", "our", or "us"), you agree to be
            bound by these Terms of Service ("Terms"). If you disagree with any
            part of these terms, you may not access the Service.
          </p>
          <p className="text-secondary-700 dark:text-secondary-300">
            These Terms apply to all visitors, users, and others who access or
            use the Service.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-secondary-800 dark:text-secondary-200 mb-4">
            2. Description of Service
          </h2>
          <p className="text-secondary-700 dark:text-secondary-300 mb-4">
            Agent Designer is an AI-powered platform for designing,
            architecting, and planning AI agents. The Service provides tools
            for:
          </p>
          <ul className="list-disc list-inside text-secondary-700 dark:text-secondary-300 space-y-1">
            <li>Defining AI agent requirements and specifications</li>
            <li>Designing agent architectures and workflows</li>
            <li>
              Generating technical specifications and implementation plans
            </li>
            <li>Creating code templates and operational documentation</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-secondary-800 dark:text-secondary-200 mb-4">
            3. User Accounts
          </h2>

          <h3 className="text-lg font-medium text-secondary-800 dark:text-secondary-200 mb-3">
            3.1 Account Creation
          </h3>
          <p className="text-secondary-700 dark:text-secondary-300 mb-4">
            To use the Service, you must create an account with a valid email
            address. You are responsible for maintaining the confidentiality of
            your account credentials and for all activities that occur under
            your account.
          </p>

          <h3 className="text-lg font-medium text-secondary-800 dark:text-secondary-200 mb-3">
            3.2 Account Requirements
          </h3>
          <ul className="list-disc list-inside text-secondary-700 dark:text-secondary-300 mb-4 space-y-1">
            <li>You must be at least 13 years old to use this Service</li>
            <li>You must provide accurate and complete information</li>
            <li>You must not share your account with others</li>
            <li>You must notify us immediately of any unauthorized access</li>
          </ul>

          <h3 className="text-lg font-medium text-secondary-800 dark:text-secondary-200 mb-3">
            3.3 Account Termination
          </h3>
          <p className="text-secondary-700 dark:text-secondary-300">
            We reserve the right to suspend or terminate your account at any
            time for violations of these Terms or for any other reason at our
            sole discretion.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-secondary-800 dark:text-secondary-200 mb-4">
            4. Acceptable Use
          </h2>
          <p className="text-secondary-700 dark:text-secondary-300 mb-4">
            You agree not to use the Service to:
          </p>
          <ul className="list-disc list-inside text-secondary-700 dark:text-secondary-300 space-y-1">
            <li>Violate any applicable laws or regulations</li>
            <li>Infringe upon intellectual property rights of others</li>
            <li>Transmit harmful, offensive, or malicious content</li>
            <li>Attempt to gain unauthorized access to our systems</li>
            <li>Interfere with or disrupt the Service or servers</li>
            <li>
              Use automated systems to access the Service without permission
            </li>
            <li>
              Design agents intended for illegal, harmful, or unethical purposes
            </li>
            <li>
              Generate content that promotes violence, discrimination, or
              harassment
            </li>
            <li>
              Attempt to reverse engineer or extract our AI models or algorithms
            </li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-secondary-800 dark:text-secondary-200 mb-4">
            5. Intellectual Property
          </h2>

          <h3 className="text-lg font-medium text-secondary-800 dark:text-secondary-200 mb-3">
            5.1 Our Intellectual Property
          </h3>
          <p className="text-secondary-700 dark:text-secondary-300 mb-4">
            The Service and its original content, features, and functionality
            are owned by GraymatterLab and are protected by international
            copyright, trademark, and other intellectual property laws.
          </p>

          <h3 className="text-lg font-medium text-secondary-800 dark:text-secondary-200 mb-3">
            5.2 Your Content
          </h3>
          <p className="text-secondary-700 dark:text-secondary-300 mb-4">
            You retain ownership of any content you create using the Service,
            including agent designs, specifications, and project data. By using
            the Service, you grant us a limited license to store, process, and
            display your content as necessary to provide the Service.
          </p>

          <h3 className="text-lg font-medium text-secondary-800 dark:text-secondary-200 mb-3">
            5.3 AI-Generated Content
          </h3>
          <p className="text-secondary-700 dark:text-secondary-300">
            Content generated by our AI features is provided for your use in
            accordance with these Terms. You are responsible for reviewing and
            validating any AI-generated content before use.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-secondary-800 dark:text-secondary-200 mb-4">
            6. AI Services Disclaimer
          </h2>
          <p className="text-secondary-700 dark:text-secondary-300 mb-4">
            The Service uses artificial intelligence (Google Vertex AI) to
            assist with agent design. You acknowledge that:
          </p>
          <ul className="list-disc list-inside text-secondary-700 dark:text-secondary-300 space-y-1">
            <li>AI-generated content may contain errors or inaccuracies</li>
            <li>
              AI outputs should be reviewed by qualified professionals before
              implementation
            </li>
            <li>
              We do not guarantee the suitability of AI-generated designs for
              any specific purpose
            </li>
            <li>
              You are solely responsible for testing and validating any agents
              built using our designs
            </li>
            <li>AI capabilities and features may change without notice</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-secondary-800 dark:text-secondary-200 mb-4">
            7. Service Availability
          </h2>
          <p className="text-secondary-700 dark:text-secondary-300 mb-4">
            We strive to maintain high availability but do not guarantee
            uninterrupted access to the Service. The Service may be temporarily
            unavailable due to:
          </p>
          <ul className="list-disc list-inside text-secondary-700 dark:text-secondary-300 space-y-1">
            <li>Scheduled maintenance and updates</li>
            <li>Emergency maintenance or security patches</li>
            <li>Technical failures or third-party service outages</li>
            <li>Circumstances beyond our reasonable control</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-secondary-800 dark:text-secondary-200 mb-4">
            8. Limitation of Liability
          </h2>
          <p className="text-secondary-700 dark:text-secondary-300 mb-4">
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, GRAYMATTERLAB SHALL NOT BE
            LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR
            PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO:
          </p>
          <ul className="list-disc list-inside text-secondary-700 dark:text-secondary-300 mb-4 space-y-1">
            <li>Loss of profits, data, or business opportunities</li>
            <li>Damages arising from use or inability to use the Service</li>
            <li>Damages arising from AI-generated content</li>
            <li>Damages arising from unauthorized access to your account</li>
          </ul>
          <p className="text-secondary-700 dark:text-secondary-300">
            Our total liability shall not exceed the amount you paid us in the
            twelve (12) months preceding the claim.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-secondary-800 dark:text-secondary-200 mb-4">
            9. Disclaimer of Warranties
          </h2>
          <p className="text-secondary-700 dark:text-secondary-300">
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT
            WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING BUT
            NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
            PARTICULAR PURPOSE, NON-INFRINGEMENT, OR COURSE OF PERFORMANCE. WE
            DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE,
            OR SECURE.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-secondary-800 dark:text-secondary-200 mb-4">
            10. Indemnification
          </h2>
          <p className="text-secondary-700 dark:text-secondary-300">
            You agree to defend, indemnify, and hold harmless GraymatterLab and
            its officers, directors, employees, and agents from any claims,
            damages, losses, or expenses arising from: (a) your use of the
            Service; (b) your violation of these Terms; (c) your violation of
            any third-party rights; or (d) any agents or systems you create
            using the Service.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-secondary-800 dark:text-secondary-200 mb-4">
            11. Pricing and Payment
          </h2>
          <p className="text-secondary-700 dark:text-secondary-300 mb-4">
            During our test user phase, the Service is provided free of charge.
            We reserve the right to introduce paid features or subscription
            plans in the future. Any pricing changes will be communicated in
            advance.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-secondary-800 dark:text-secondary-200 mb-4">
            12. Modifications to Service
          </h2>
          <p className="text-secondary-700 dark:text-secondary-300">
            We reserve the right to modify, suspend, or discontinue the Service
            (or any part thereof) at any time with or without notice. We shall
            not be liable to you or any third party for any modification,
            suspension, or discontinuation of the Service.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-secondary-800 dark:text-secondary-200 mb-4">
            13. Governing Law
          </h2>
          <p className="text-secondary-700 dark:text-secondary-300">
            These Terms shall be governed by and construed in accordance with
            the laws of the State of California, United States, without regard
            to its conflict of law provisions. Any disputes arising from these
            Terms or the Service shall be resolved in the courts of California.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-secondary-800 dark:text-secondary-200 mb-4">
            14. Changes to Terms
          </h2>
          <p className="text-secondary-700 dark:text-secondary-300">
            We reserve the right to modify these Terms at any time. We will
            notify users of any material changes by posting the updated Terms on
            this page and updating the "Last updated" date. Your continued use
            of the Service after such modifications constitutes acceptance of
            the updated Terms.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-secondary-800 dark:text-secondary-200 mb-4">
            15. Severability
          </h2>
          <p className="text-secondary-700 dark:text-secondary-300">
            If any provision of these Terms is found to be unenforceable or
            invalid, that provision shall be limited or eliminated to the
            minimum extent necessary, and the remaining provisions shall remain
            in full force and effect.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-secondary-800 dark:text-secondary-200 mb-4">
            16. Contact Information
          </h2>
          <p className="text-secondary-700 dark:text-secondary-300">
            If you have questions about these Terms, please contact us at:
          </p>
          <div className="mt-4 p-4 bg-secondary-50 dark:bg-secondary-800 rounded-lg">
            <p className="text-secondary-700 dark:text-secondary-300">
              <strong>GraymatterLab</strong>
              <br />
              Email:{' '}
              <a
                href="mailto:legal@graymatterlab.ai"
                className="text-primary-600 hover:text-primary-700 dark:text-primary-400"
              >
                legal@graymatterlab.ai
              </a>
            </p>
          </div>
        </section>

        <section className="mb-8 p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg border border-primary-200 dark:border-primary-800">
          <p className="text-secondary-700 dark:text-secondary-300 text-sm">
            By using Agent Designer, you acknowledge that you have read,
            understood, and agree to be bound by these Terms of Service and our{' '}
            <Link
              to="/privacy"
              className="text-primary-600 hover:text-primary-700 dark:text-primary-400"
            >
              Privacy Policy
            </Link>
            .
          </p>
        </section>
      </article>
    </div>
  );
};

export default TermsOfServicePage;
